import { createTanstackQueryUtils } from "@orpc/tanstack-query";
import {
  accountDefaultTemplateSchema,
  accountDefaultTemplateUpdateSchema,
} from "@rosc/contracts";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { referenceClient } from "@/lib/orpc/client";
import { queryClient } from "@/lib/query-client";
import {
  isBuiltInTemplate,
  readLocalDefault,
  removeLocalDefault,
  resolveDefaultTemplate,
  useTemplatePreferenceVersion,
} from "@/lib/template-preferences";
import {
  refreshTemplateReads,
  useOptionalTemplateList,
} from "@/lib/template-queries";
import {
  getVisitScope,
  useVisitScope,
  type VisitScope,
} from "@/lib/visit-queries";

const orpc = createTanstackQueryUtils(referenceClient, {
  scoped: {
    myDefaultTemplate: {
      queryInterceptors: [
        async (options) =>
          accountDefaultTemplateSchema.parse(await options.next()),
      ],
    },
  },
});
const key = (scope: VisitScope | null) => [
  "template-reads",
  scope?.uid ?? "",
  scope?.accountPublicId ?? "",
  scope?.generation ?? 0,
  "account-default",
];
// The account default's cache key (Storybook seeds it).
export const accountDefaultTemplateKey = key;
const moved = new WeakSet<VisitScope>();

export async function saveAccountDefaultTemplate(
  scope: VisitScope,
  value: string,
): Promise<boolean> {
  if (getVisitScope() !== scope) return false;
  try {
    const body = accountDefaultTemplateUpdateSchema.parse(
      isBuiltInTemplate(value)
        ? { template: value }
        : { customTemplatePublicId: value },
    );
    const result = accountDefaultTemplateSchema.parse(
      await referenceClient.setMyDefaultTemplate(
        { body, query: {} },
        { context: { expectedUserUid: scope.uid } },
      ),
    );
    if (getVisitScope() !== scope) return false;
    await queryClient.cancelQueries({ queryKey: key(scope) });
    if (getVisitScope() !== scope) return false;
    queryClient.setQueryData(key(scope), result);
    await refreshTemplateReads(scope);
    return getVisitScope() === scope;
  } catch {
    return false;
  }
}

export function useAccountDefaultTemplate(enabled = true): {
  value: string;
  loading: boolean;
  error: boolean;
} {
  const scope = useVisitScope();
  useTemplatePreferenceVersion();
  const active = enabled && !!scope;
  const list = useOptionalTemplateList(enabled);
  const query = useQuery(
    orpc.myDefaultTemplate.queryOptions({
      queryKey: key(scope),
      input: {},
      context: { expectedUserUid: scope?.uid },
      enabled: active,
      staleTime: 30_000,
      retry: false,
    }),
    queryClient,
  );
  const data = active && !query.isError ? query.data : undefined;
  const local =
    scope && data?.source === "fallback"
      ? readLocalDefault(scope.accountPublicId)
      : null;
  const localDefault =
    local &&
    (isBuiltInTemplate(local) ||
      (!list.loading &&
        !list.error &&
        list.templates.some((template) => template.public_id === local)))
      ? local
      : null;

  // Every successful read retires stale keys, even after a failed move.
  useEffect(() => {
    if (!scope || !data || !query.dataUpdatedAt || data.source === "fallback")
      return;
    moved.add(scope);
    removeLocalDefault(scope.accountPublicId);
  }, [scope, data, query.dataUpdatedAt]);

  useEffect(() => {
    if (!scope || !data || moved.has(scope)) return;
    // A custom id can be checked only against the complete owned list.
    if (
      data.source === "fallback" &&
      local &&
      !isBuiltInTemplate(local) &&
      (list.loading || list.error)
    )
      return;
    moved.add(scope);
    if (!localDefault) {
      removeLocalDefault(scope.accountPublicId);
      return;
    }
    void saveAccountDefaultTemplate(scope, localDefault).then((saved) => {
      if (saved) removeLocalDefault(scope.accountPublicId);
    });
  }, [scope, data, local, localDefault, list.loading, list.error]);

  return {
    value:
      localDefault ??
      data?.customTemplatePublicId ??
      data?.template ??
      resolveDefaultTemplate(list.templates, scope?.accountPublicId),
    loading: active && query.isPending,
    error: active && query.isError,
  };
}
